/**
 * Ask the case (PIVOT02): what a citation chip on a pivot statement points at.
 *
 * A statement cites record facts (`F-...`, `R-...`), rows the pivot found (`PV-<round>-<n>`) and transaction hashes. The
 * chip resolves to the thing the officer can check: the record row for a fact id, the pivot row for a `PV-` id, and for a
 * hash the ledger row that carries it (with its hash) or else the pivot row or the record fact that does. Pure, so the
 * panel and its test share one answer.
 */
import type { PivotBlock, PivotRow } from '../../types/pivot';

export type CiteKind = 'fact' | 'ledger-row' | 'pivot-row' | 'hash';

export interface ResolvedCite {
  id: string;
  kind: CiteKind;
  /** Short name for the chip's tooltip and the resolved line: "record row", "ledger row", "page-read row", "hash". */
  label: string;
  text: string;
  /** The transaction hashes of the row (a ledger row names its hash). */
  hashes: string[];
  rowId?: string;
  /** The record fact ids that print the hash (only for a bare hash). */
  facts?: string[];
}

const isLedger = (r: PivotRow) => r.fields?.source === 'ledger';

function fromRow(id: string, r: PivotRow): ResolvedCite {
  return isLedger(r)
    ? { id, kind: 'ledger-row', label: 'ledger row', text: r.text, hashes: r.hashes || [], rowId: r.id }
    : { id, kind: 'pivot-row', label: `${r.tool} row`, text: r.text, hashes: r.hashes || [], rowId: r.id };
}

export function resolveCite(block: PivotBlock, id: string): ResolvedCite {
  const rows = block.rows || [];
  const cited = (block.cited || {})[id];
  const row = rows.find((r) => r.id === id);
  if (row) return fromRow(id, row);
  if (cited?.kind === 'fact') {
    return { id, kind: 'fact', label: 'record row', text: `${cited.block ? `[${cited.block}] ` : ''}${cited.text || ''}`.trim(), hashes: [] };
  }
  // a bare hash: the ledger row that carries it first, then any pivot row, then the record facts that print it
  const carrying = rows.filter((r) => (r.hashes || []).some((h) => h.toLowerCase() === id.toLowerCase()));
  const ledgerRow = carrying.find(isLedger);
  if (ledgerRow) return { ...fromRow(id, ledgerRow), hashes: [id] };
  if (carrying.length) return { ...fromRow(id, carrying[0]), hashes: [id] };
  const facts = cited?.facts || [];
  return { id, kind: 'hash', label: facts.length ? 'record hash' : 'hash', hashes: [id], facts,
    text: facts.length ? `Printed by record row ${facts.join(', ')}.` : 'A transaction hash the record or a tool result lists.' };
}

export const shortId = (s: string) => (s.length > 14 ? `${s.slice(0, 8)}…${s.slice(-4)}` : s);
